import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/services/api';
import { Test, Task, Evaluation } from '@/types';
import PageLayout from '@/shared/ui/layout/PageLayout';
import GroupedContent from '@/shared/ui/layout/GroupedContent';
import { DetailSection } from '@/shared/ui/layout';
import './styles.css';

// Helper function to group tasks by category
const groupTasksByCategory = (tasks: Task[]) => {
  const grouped: Record<string, Task[]> = {};
  
  tasks.forEach(task => {
    const category = task.category || 'Uncategorized';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(task);
  });
  
  // Sort categories alphabetically
  return Object.keys(grouped)
    .sort()
    .reduce((acc, category) => {
      acc[category] = grouped[category];
      return acc;
    }, {} as Record<string, Task[]>);
};

const TestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [test, setTest] = useState<Test | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [groupedTasks, setGroupedTasks] = useState<Record<string, Task[]>>({});
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [hasEvaluations, setHasEvaluations] = useState(false);
  const [evaluationModels, setEvaluationModels] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) return;
        
        // Fetch test details
        const testData = await api.tests.getById(id);
        setTest(testData);

        // Fetch full task details for each task in the test
        const taskIds = testData.tasks.map((t: string | Task) => 
          typeof t === 'string' ? t : t._id
        );
        const taskDetails = await Promise.all(
          taskIds.filter((id): id is string => id !== undefined)
            .map(taskId => api.tasks.getById(taskId))
        );
        setTasks(taskDetails);
        
        // Group tasks by category
        setGroupedTasks(groupTasksByCategory(taskDetails));
        
        // Check if test has evaluations
        const allEvaluations = await api.evaluations.getAll();
        const testEvaluations = allEvaluations.filter(
          (evaluation: any) => evaluation.test === id || (evaluation.test && evaluation.test._id === id)
        );
        
        setEvaluations(testEvaluations);
        setHasEvaluations(testEvaluations.length > 0);
        
        if (testEvaluations.length > 0) {
          // Extract unique model names
          const models = testEvaluations
            .map((evaluation: any) => evaluation.modelName)
            .filter((name: string, index: number, self: string[]) => 
              self.indexOf(name) === index
            );
          setEvaluationModels(models);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to fetch test');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleDelete = async () => {
    if (!test?._id || !window.confirm('Are you sure you want to delete this test?')) return;

    try {
      await api.tests.delete(test._id);
      toast.success('Test deleted successfully!');
      navigate('/tests');
    } catch (err: unknown) {
      const apiError = err as ApiError;
      let errorMessage = apiError.message || 'Failed to delete test';

      if (apiError.data && apiError.data.evaluations) {
        const modelNames = apiError.data.evaluations
          .map((evaluation) => evaluation.modelName)
          .filter((name: string, index: number, self: string[]) => self.indexOf(name) === index)
          .join(', ');
        errorMessage = `Cannot delete test: It has evaluations for the following models: ${modelNames}`;
      }
      
      toast.error(errorMessage);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!test) return <div>Test not found</div>;

  // Render function for task groups
  const renderTaskGroup = (category: string, tasks: Task[]) => (
    <div className="results-list">
      {tasks.map(task => (
        <div 
          key={task._id} 
          className="task-evaluation-card"
          onClick={() => navigate(`/tasks/${task._id}`)}
        >
          <h3 className="task-title">{task.title}</h3>
          
          <div className="task-content">
            <div className="content-box">
              <div className="text-content">
                {task.content}
              </div>
            </div>
            
            {/* Display task attachments if any */}
            {task.attachments && task.attachments.length > 0 && (
              <div className="task-attachments">
                <div className="attachments-list">
                  {task.attachments.map((attachment) => {
                    const isImage = attachment.mimetype.startsWith('image/');
                    const isPdf = attachment.mimetype === 'application/pdf';
                    const downloadUrl = api.tasks.getAttachmentUrl(task._id!, attachment._id!);
                    
                    // Function to handle drag start
                    const handleDragStart = (e: React.DragEvent) => {
                      e.stopPropagation(); // Prevent card click when dragging
                      // Set the drag data with the download URL and filename
                      e.dataTransfer.setData('text/uri-list', downloadUrl);
                      e.dataTransfer.setData('text/plain', downloadUrl);
                      e.dataTransfer.setData('DownloadURL', `${attachment.mimetype}:${attachment.originalname}:${downloadUrl}`);
                      // Set the drag effect to 'copy' to indicate a file copy operation
                      e.dataTransfer.effectAllowed = 'copy';
                    };
                    
                    return (
                      <div 
                        key={attachment._id} 
                        className="attachment-item"
                        draggable="true"
                        onDragStart={handleDragStart}
                        onClick={(e) => e.stopPropagation()} // Prevent card click when clicking attachment
                        title="Drag to desktop or other applications"
                      >
                        {isImage ? (
                          <div className="image-preview">
                            <img src={downloadUrl} alt={attachment.originalname} />
                          </div>
                        ) : (
                          <div className="file-icon">
                            {isPdf ? '📄 PDF' : '📄'}
                          </div>
                        )}
                        <div className="attachment-info">
                          <span className="attachment-name">{attachment.originalname}</span>
                          <a 
                            href={downloadUrl} 
                            download={attachment.originalname}
                            className="download-link"
                            onClick={(e) => e.stopPropagation()} // Prevent card click when clicking download
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {task.scoringMethod === 'numerical' && task.scoringRules && (
            <div className="scoring-rules">
              <h4>Scoring Rules:</h4>
              <div className="content-box">
                {task.scoringRules}
              </div>
            </div>
          )}

          <div className="task-footer">
            <div className="task-meta">
              <span className="scoring-method">
                {task.scoringMethod === 'numerical' 
                  ? `Numerical (${task.scoringScale?.min} - ${task.scoringScale?.max})` 
                  : 'Binary (Pass/Fail)'}
              </span>
              {task.solution && (
                <div className="solution-preview">
                  <h4>Solution Available</h4>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const actions = (
    <div className="actions-group">
      <Link to={`/tests/${test._id}/evaluate`} className="button primary">
        Evaluate
      </Link>
      
      <div className="actions-right">
        <div className="tooltip-container" data-position="bottom">
          <button 
            onClick={() => !hasEvaluations && navigate(`/tests/${test._id}/edit`)} 
            className={`button edit-primary ${hasEvaluations ? 'disabled' : ''}`}
          >
            Edit
          </button>
          {hasEvaluations && (
            <div className="tooltip">
              Cannot edit test: It has evaluations for {evaluationModels.join(', ')}
            </div>
          )}
        </div>
        
        <div className="tooltip-container" data-position="bottom">
          <button 
            onClick={hasEvaluations ? undefined : handleDelete} 
            className={`button danger ${hasEvaluations ? 'disabled' : ''}`}
          >
            Delete
          </button>
          {hasEvaluations && (
            <div className="tooltip">
              Cannot delete test: It has evaluations for {evaluationModels.join(', ')}
            </div>
          )}
        </div>
        
        <Link to="/tests" className="button">
          Back
        </Link>
      </div>
    </div>
  );

  const detailItems = [
    {
      label: 'Priority:',
      value: test.priority ?? 'Not set'
    },
    {
      label: 'Created:',
      value: new Date(test.createdAt!).toLocaleString()
    },
    ...(test.updatedAt ? [
      {
        label: 'Last Updated:',
        value: new Date(test.updatedAt).toLocaleString()
      }
    ] : []),
    {
      label: 'Number of Tasks:',
      value: tasks.length
    },
    {
      label: 'Categories:',
      value: Object.keys(groupedTasks).length
    },
    {
      label: 'Description:',
      value: test.description
    }
  ];

  return (
    <PageLayout
      title={test.name}
      actions={actions}
    >
      <div className="test-content">
        <DetailSection items={detailItems} />

        <h3>Tasks by Category</h3>
        {tasks.length === 0 ? (
          <p>No tasks in this test.</p>
        ) : (
          <GroupedContent
            groups={groupedTasks}
            renderGroup={renderTaskGroup}
            emptyMessage="No tasks in this test."
          />
        )}
      </div>
    </PageLayout>
  );
};

export default TestDetail; 